import * as path from 'node:path';
import { getBridgeRoot } from '../../../files/cwd/index.js';
import {
  getWorkingTreeChangeFilePatch,
  type WorkingTreeChangeFileRow,
  type WorkingTreeRepoChangesBasis,
} from '../../../git/changes/index.js';
import { resolveRepoPathRelativeToWorkspaceRoot } from '../../../git/changes/repo/resolve-repo-path-relative-to-workspace-root.js';
import { normalizeRepoPathRelativeToWorkspaceRoot } from '../../../git/changes/repo/normalize-repo-path-relative-to-workspace-root.js';
import { forEachWithGitYield } from '../../../git/git-runtime.js';
import { isGitRepoDirectory } from '../../../git/is-git-repo.js';
import type { SessionWorktreeCache } from '../session-worktree-cache.js';
import { resolveCommitTargetsAsync } from '../resolve-commit-targets.js';
import type { SessionDiscover } from './types.js';

export async function getSessionWorkingTreeChangeFilePatch(
  cache: SessionWorktreeCache,
  sessionId: string,
  discover: SessionDiscover,
  opts: {
    repoRelPath: string;
    workspaceRelPath: string;
    change: WorkingTreeChangeFileRow['change'];
    movedFromWorkspaceRelPath?: string | null;
    basis: WorkingTreeRepoChangesBasis;
  },
): Promise<{ patchContent?: string; totalLines: number | null }> {
  const targets = await resolveCommitTargetsAsync(sessionId, cache, discover);

  const bridgeRoot = path.resolve(getBridgeRoot());
  const sessionParentPath = cache.getSessionParentPath(sessionId)
    ? path.resolve(cache.getSessionParentPath(sessionId)!)
    : null;
  const repoPathRelativeToWorkspaceRootFilter = normalizeRepoPathRelativeToWorkspaceRoot(
    opts.repoRelPath.trim(),
  );

  let result: { patchContent?: string; totalLines: number | null } | null = null;
  await forEachWithGitYield(targets, async (targetPath) => {
    if (result) return;
    const checkoutPath = path.resolve(targetPath);
    if (!(await isGitRepoDirectory(checkoutPath))) return;
    const repoPathRelativeToWorkspaceRoot = await resolveRepoPathRelativeToWorkspaceRoot({
      checkoutPath,
      bridgeRoot,
      sessionParentPath,
    });
    if (repoPathRelativeToWorkspaceRoot !== repoPathRelativeToWorkspaceRootFilter) return;

    result = await getWorkingTreeChangeFilePatch({
      repoGitCwd: checkoutPath,
      repoPathRelativeToWorkspaceRoot: repoPathRelativeToWorkspaceRootFilter,
      filePathRelativeToWorkspaceRoot: opts.workspaceRelPath.trim(),
      change: opts.change,
      movedFromFilePathRelativeToWorkspaceRoot: opts.movedFromWorkspaceRelPath?.trim() || null,
      basis: opts.basis,
    });
  });

  if (!result) throw new Error(`Repository not found: ${repoPathRelativeToWorkspaceRootFilter}`);
  return result;
}
